import React, { useEffect, useState } from 'react';
import { IonPage, IonContent, useIonToast, useIonRouter } from '@ionic/react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useLogin } from '@/hooks/useAuth';
import { useAuthStore } from '@/store/authStore';
import { useTenantStore } from '@/store/tenantStore';
import { defaultLandingPath } from '@/utils/landing';
import '@/theme/login.css';

/**
 * Where this account belongs after signing in. Read from the store rather
 * than from a hook so it is correct in the same tick the login response
 * lands — a Cina volunteer holds only communion.record and must go straight
 * to the check-in screen, never through the dashboard.
 */
function landingPath(): string {
  const user = useAuthStore.getState().user;
  if (!user) return '/';
  const granted = new Set(user.permissions ?? []);
  return defaultLandingPath((permission) => granted.has(permission));
}

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email-ul este obligatoriu')
    .email('Introduceți un email valid'),
  password: z
    .string()
    .min(1, 'Parola este obligatorie')
    .min(6, 'Parola trebuie să aibă minim 6 caractere'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const router = useIonRouter();
  const loginMutation = useLogin();
  const { isAuthenticated } = useAuthStore();
  const { tenantName } = useTenantStore();
  const [presentToast] = useIonToast();
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const emailValue = watch('email');
  const passwordValue = watch('password');

  useEffect(() => {
    if (isAuthenticated) {
      router.push(landingPath(), 'root', 'replace');
    }
  }, [isAuthenticated, router]);

  const onSubmit = async (data: LoginFormData) => {
    try {
      await loginMutation.mutateAsync({
        email: data.email,
        password: data.password,
      });
      router.push(landingPath(), 'root', 'replace');
    } catch {
      void presentToast({
        message: 'Email sau parolă incorectă. Încercați din nou.',
        duration: 3000,
        color: 'danger',
        position: 'top',
      });
    }
  };

  const isPending = isSubmitting || loginMutation.isPending;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="login-split">
          {/* ── Left: Form Panel ── */}
          <div className="login-form-panel">
            <div className="login-form-inner">
              {/* Header */}
              <div className="login-header login-animate login-animate--1">
                <h1 className="login-title">
                  Bine ați venit.
                  <br />
                  Autentificați-vă pentru a continua.
                </h1>
                <p className="login-subtitle">
                  {tenantName
                    ? `Introduceți datele pentru ${tenantName}`
                    : 'Introduceți datele de acces'}
                </p>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="login-fields">
                  {/* Email */}
                  <div
                    className={`login-field login-animate login-animate--2${
                      emailValue ? ' login-field--has-value' : ''
                    }`}
                  >
                    <input
                      {...register('email')}
                      type="email"
                      className="login-field__input"
                      placeholder=" "
                      autoComplete="email"
                      autoFocus
                    />
                    <label className="login-field__label">Email</label>
                    <span className="login-field__icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="4" width="20" height="16" rx="2" />
                        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                      </svg>
                    </span>
                    {errors.email && (
                      <span className="login-field__error">{errors.email.message}</span>
                    )}
                  </div>

                  {/* Password */}
                  <div
                    className={`login-field login-animate login-animate--3${
                      passwordValue ? ' login-field--has-value' : ''
                    }`}
                  >
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      className="login-field__input"
                      placeholder=" "
                      autoComplete="current-password"
                    />
                    <label className="login-field__label">Parolă</label>
                    <button
                      type="button"
                      className="login-field__toggle-pw"
                      onClick={() => setShowPassword(!showPassword)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Ascunde parola' : 'Arată parola'}
                    >
                      {showPassword ? (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                          <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                        </svg>
                      ) : (
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                    {errors.password && (
                      <span className="login-field__error">{errors.password.message}</span>
                    )}
                  </div>
                </div>

                {/* Forgot Password */}
                <a
                  className="login-forgot login-animate login-animate--4"
                  href="/forgot-password"
                  onClick={(e) => {
                    e.preventDefault();
                    router.push('/forgot-password');
                  }}
                >
                  Ai uitat parola?
                </a>

                {/* Buttons */}
                <div className="login-actions login-animate login-animate--5">
                  <button
                    type="submit"
                    className="login-btn login-btn--primary"
                    disabled={isPending}
                  >
                    {isPending && <span className="login-spinner" />}
                    Autentificare
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* ── Right: Brand Panel ── */}
          <div className="login-brand-panel">
            {/* Floating decorative shapes */}
            <div className="login-brand-panel__shapes">
              <div className="login-decor login-decor--1" />
              <div className="login-decor login-decor--2" />
              <div className="login-decor login-decor--3" />
              <div className="login-decor login-decor--4" />
              <div className="login-decor login-decor--5" />
            </div>

            {/* Brand content */}
            <div className="login-brand-content">
              <div className="login-brand-logo">
                <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <h2 className="login-brand-name">Betel Gestiune</h2>
              <p className="login-brand-tagline">
                Platforma completă pentru administrarea eficientă a comunității bisericii
              </p>
              <div className="login-brand-features">
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Evidența membrilor și familiilor
                </div>
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Grupuri, sectoare și activități
                </div>
                <div className="login-brand-feature">
                  <span className="login-brand-feature__dot" />
                  Rapoarte și statistici în timp real
                </div>
              </div>
            </div>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default LoginPage;
